/**
 * Letter Glitch Background Effect
 * Basado en ReactBits - Adaptado para Shopify
 * Efecto de fondo global con canvas y caracteres aleatorios
 */

class LetterGlitchBackground {
  constructor(options = {}) {
    // Configuración basada en la referencia de React Bits
    this.config = {
      glitchColors: options.glitchColors || ['#2b4539', '#61dca3', '#61b3dc'],
      glitchSpeed: options.glitchSpeed || 50,
      centerVignette: options.centerVignette || false,
      outerVignette: options.outerVignette !== false, // true por defecto
      smooth: options.smooth !== false, // true por defecto
      characters: options.characters || 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$&*()-_+=/[]{};:<>.,0123456789',
      // Mensajes formados en el propio canvas
      displayPhrase: options.displayPhrase !== false, // true por defecto
      cyclicTexts: options.cyclicTexts || [],
      phraseFadeDuration: options.phraseFadeDuration || 700, // ms
      phraseHoldDuration: options.phraseHoldDuration || 2500, // ms
      phraseIntervalGap: options.phraseIntervalGap || 1000, // ms
      phraseAlwaysVisible: options.phraseAlwaysVisible !== false, // true por defecto (sin fade)
      phraseContrastColor: options.phraseContrastColor || '#FAF5EF',
      phraseShadowBlur: options.phraseShadowBlur || 6,
      // Cobertura en toda la página
      phraseTileHorizontally: options.phraseTileHorizontally !== false, // repetir varias veces por fila
      phraseRowStep: options.phraseRowStep || 2, // colocar frase cada N filas
      phraseGap: options.phraseGap || 4, // espacio en columnas entre repeticiones
      // Tamaño y proporciones de fuente (intermedio y estético)
      fontSizeDivisor: options.fontSizeDivisor || 40, // intermedio entre ~35 (grande) y 45 (actual)
      charWidthFactor: options.charWidthFactor || 0.85, // ligeramente más ancho
      charHeightFactor: options.charHeightFactor || 1.15, // ligeramente menos alto para equilibrio
      ...options
    };

    // Referencias del DOM
    this.canvasRef = null;
    this.animationRef = null;
    this.letters = [];
    this.grid = { columns: 0, rows: 0 };
    this.context = null;
    this.lastGlitchTime = Date.now();

    // Configuración del canvas - valores de la referencia
    this.fontSize = 16;
    this.charWidth = 10;
    this.charHeight = 20;

    // Array de caracteres
    this.lettersAndSymbols = Array.from(this.config.characters);

    // Estado para formar frases con el grid
    this.phraseIndices = new Set();
    this.currentPhraseIndex = 0;
    this.phraseAlpha = 0; // 0..1
    this.phraseState = 'idle'; // idle | fading_in | holding | fading_out
    this.phraseStartTime = 0;
    this.lastPhraseSwitchTime = Date.now();
    this.nextPhraseScheduled = false;
    
    this.init();
  }

  init() {
    console.log('Inicializando LetterGlitchBackground...');
    
    this.canvasRef = document.getElementById('glitch-canvas');
    if (!this.canvasRef) {
      console.error('Canvas #glitch-canvas no encontrado');
      return;
    }

    this.context = this.canvasRef.getContext('2d');
    if (!this.context) {
      console.error('No se pudo obtener el contexto 2D del canvas');
      return;
    }

    this.setupCanvas();
    this.setupVignettes();
    this.start();
    
    console.log('LetterGlitchBackground inicializado correctamente');
  }

  setupCanvas() {
    this.resizeCanvas();
    
    // Event listener para redimensionar
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.animationRef) {
          cancelAnimationFrame(this.animationRef);
        }
        this.resizeCanvas();
        this.animate();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
  }

  setupVignettes() {
    const outerVignette = document.getElementById('outer-vignette');
    const centerVignette = document.getElementById('center-vignette');
    
    if (outerVignette) {
      outerVignette.style.display = this.config.outerVignette ? 'block' : 'none';
    }
    
    if (centerVignette) {
      centerVignette.style.display = this.config.centerVignette ? 'block' : 'none';
    }
  }

  getRandomChar() {
    return this.lettersAndSymbols[Math.floor(Math.random() * this.lettersAndSymbols.length)];
  }

  getRandomColor() {
    return this.config.glitchColors[Math.floor(Math.random() * this.config.glitchColors.length)];
  }

  hexToRgb(hex) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  interpolateColor(start, end, factor) {
    const result = {
      r: Math.round(start.r + (end.r - start.r) * factor),
      g: Math.round(start.g + (end.g - start.g) * factor),
      b: Math.round(start.b + (end.b - start.b) * factor)
    };
    return `rgb(${result.r}, ${result.g}, ${result.b})`;
  }

  calculateGrid(width, height) {
    const columns = Math.ceil(width / this.charWidth);
    const rows = Math.ceil(height / this.charHeight);
    return { columns, rows };
  }

  initializeLetters(columns, rows) {
    this.grid = { columns, rows };
    const totalLetters = columns * rows;
    this.letters = Array.from({ length: totalLetters }, () => ({
      char: this.getRandomChar(),
      color: this.getRandomColor(),
      targetColor: this.getRandomColor(),
      colorProgress: 1
    }));
  }

  resizeCanvas() {
    if (!this.canvasRef) return;
    
    const parent = this.canvasRef.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();

    this.canvasRef.width = rect.width * dpr;
    this.canvasRef.height = rect.height * dpr;

    this.canvasRef.style.width = `${rect.width}px`;
    this.canvasRef.style.height = `${rect.height}px`;

    if (this.context) {
      this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Métricas responsivas para legibilidad
    const base = Math.max(14, Math.floor(rect.width / this.config.fontSizeDivisor));
    this.fontSize = base; // tamaño de fuente
    this.charWidth = Math.max(8, Math.round(base * this.config.charWidthFactor));
    this.charHeight = Math.max(12, Math.round(base * this.config.charHeightFactor));

    const { columns, rows } = this.calculateGrid(rect.width, rect.height);
    this.initializeLetters(columns, rows);

    this.drawLetters();
  }

  drawLetters() {
    if (!this.context || this.letters.length === 0) return;
    
    const ctx = this.context;
    const { width, height } = this.canvasRef.getBoundingClientRect();
    
    // Limpiar canvas
    ctx.clearRect(0, 0, width, height);
    
    // Configurar fuente
    ctx.font = `${this.fontSize}px monospace`;
    ctx.textBaseline = 'top';

    this.letters.forEach((letter, index) => {
      const x = (index % this.grid.columns) * this.charWidth;
      const y = Math.floor(index / this.grid.columns) * this.charHeight;
      
      // Si es parte de la frase activa, aplicar contraste y sombra
      if (this.phraseIndices.has(index)) {
        ctx.globalAlpha = this.config.phraseAlwaysVisible ? 1 : this.phraseAlpha;
        ctx.fillStyle = this.config.phraseContrastColor;
        ctx.shadowColor = this.config.phraseContrastColor;
        ctx.shadowBlur = this.config.phraseShadowBlur;
      } else {
        ctx.globalAlpha = 1;
        ctx.fillStyle = letter.color;
        ctx.shadowBlur = 0;
      }

      ctx.fillText(letter.char, x, y);
    });

    // Restaurar estado
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  }

  updateLetters() {
    if (!this.letters || this.letters.length === 0) return;

    const updateCount = Math.max(1, Math.floor(this.letters.length * 0.05));

    for (let i = 0; i < updateCount; i++) {
      const index = Math.floor(Math.random() * this.letters.length);
      if (!this.letters[index]) continue;

      // No alterar letras que forman la frase activa
      if (this.phraseIndices.has(index)) continue;

      this.letters[index].char = this.getRandomChar();
      this.letters[index].targetColor = this.getRandomColor();

      if (!this.config.smooth) {
        this.letters[index].color = this.letters[index].targetColor;
        this.letters[index].colorProgress = 1;
      } else {
        this.letters[index].colorProgress = 0;
      }
    }
  }

  handleSmoothTransitions() {
    let needsRedraw = false;
    this.letters.forEach(letter => {
      if (letter.colorProgress < 1) {
        letter.colorProgress += 0.05;
        if (letter.colorProgress > 1) letter.colorProgress = 1;

        const startRgb = this.hexToRgb(letter.color);
        const endRgb = this.hexToRgb(letter.targetColor);
        if (startRgb && endRgb) {
          letter.color = this.interpolateColor(startRgb, endRgb, letter.colorProgress);
          needsRedraw = true;
        }
      }
    });

    if (needsRedraw) {
      this.drawLetters();
    }
  }

  animate() {
    const now = Date.now();
    if (now - this.lastGlitchTime >= this.config.glitchSpeed) {
      this.updateLetters();
      this.drawLetters();
      this.lastGlitchTime = now;
    }

    if (this.config.smooth) {
      this.handleSmoothTransitions();
    }

    // Ciclo de frases
    this.runPhraseCycle(now);

    this.animationRef = requestAnimationFrame(() => this.animate());
  }

  start() {
    if (!this.animationRef) {
      this.animate();
    }
  }

  stop() {
    if (this.animationRef) {
      cancelAnimationFrame(this.animationRef);
      this.animationRef = null;
    }
  }

  destroy() {
    this.stop();
    window.removeEventListener('resize', this.resizeCanvas);
  }

  // Método para actualizar configuración
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.lettersAndSymbols = Array.from(this.config.characters);
    this.setupVignettes();
  }

  // ===== Formar frases en el grid =====
  applyPhraseToGrid(phrase) {
    if (!this.grid || !this.grid.columns || !this.grid.rows) return;
    if (!phrase || !phrase.length) return;

    this.phraseIndices.clear();

    const cols = this.grid.columns;
    const rows = this.grid.rows;
    const rowStep = Math.max(1, this.config.phraseRowStep);
    const gap = Math.max(0, this.config.phraseGap);

    // Repetir la frase a lo largo de múltiples filas
    for (let row = 0; row < rows; row += rowStep) {
      if (this.config.phraseTileHorizontally) {
        // Repetir horizontalmente con espacios
        let col = 0;
        while (col + phrase.length <= cols) {
          for (let i = 0; i < phrase.length; i++) {
            const index = row * cols + (col + i);
            this.phraseIndices.add(index);
            if (this.letters[index]) {
              this.letters[index].char = phrase[i];
              this.letters[index].color = this.config.phraseContrastColor;
              this.letters[index].targetColor = this.config.phraseContrastColor;
              this.letters[index].colorProgress = 1;
            }
          }
          col += phrase.length + gap;
        }
      } else {
        // Una sola vez por fila, centrada
        const startCol = Math.max(0, Math.floor((cols - phrase.length) / 2));
        for (let i = 0; i < phrase.length && (startCol + i) < cols; i++) {
          const index = row * cols + (startCol + i);
          this.phraseIndices.add(index);
          if (this.letters[index]) {
            this.letters[index].char = phrase[i];
            this.letters[index].color = this.config.phraseContrastColor;
            this.letters[index].targetColor = this.config.phraseContrastColor;
            this.letters[index].colorProgress = 1;
          }
        }
      }
    }
  }

  clearPhrase() {
    this.phraseIndices.clear();
    this.phraseAlpha = 0;
  }

  runPhraseCycle(now) {
    if (!this.config.displayPhrase || !Array.isArray(this.config.cyclicTexts) || this.config.cyclicTexts.length === 0) {
      return;
    }

    const fadeDur = this.config.phraseFadeDuration;
    const holdDur = this.config.phraseHoldDuration;
    const gapDur = this.config.phraseIntervalGap;

    if (this.config.phraseAlwaysVisible) {
      // Sin transiciones: mantener visible y cambiar por intervalo
      if (this.phraseState === 'idle' && (now - this.lastPhraseSwitchTime > gapDur)) {
        const phrase = this.config.cyclicTexts[this.currentPhraseIndex % this.config.cyclicTexts.length];
        this.applyPhraseToGrid(phrase);
        this.phraseAlpha = 1;
        this.phraseState = 'holding';
        this.phraseStartTime = now;
      } else if (this.phraseState === 'holding' && (now - this.phraseStartTime >= holdDur)) {
        this.clearPhrase();
        this.lastPhraseSwitchTime = now;
        this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.config.cyclicTexts.length;
        this.phraseState = 'idle';
      }
      return;
    }

    // Con transiciones (fade in/out)
    switch (this.phraseState) {
      case 'idle': {
        if (!this.nextPhraseScheduled && (now - this.lastPhraseSwitchTime > gapDur)) {
          const phrase = this.config.cyclicTexts[this.currentPhraseIndex % this.config.cyclicTexts.length];
          this.applyPhraseToGrid(phrase);
          this.phraseStartTime = now;
          this.phraseState = 'fading_in';
          this.nextPhraseScheduled = true;
        }
        break;
      }
      case 'fading_in': {
        const progress = Math.min(1, (now - this.phraseStartTime) / fadeDur);
        this.phraseAlpha = progress;
        if (progress >= 1) {
          this.phraseState = 'holding';
          this.phraseStartTime = now;
        }
        break;
      }
      case 'holding': {
        if (now - this.phraseStartTime >= holdDur) {
          this.phraseState = 'fading_out';
          this.phraseStartTime = now;
        }
        break;
      }
      case 'fading_out': {
        const progress = Math.min(1, (now - this.phraseStartTime) / fadeDur);
        this.phraseAlpha = 1 - progress;
        if (progress >= 1) {
          this.clearPhrase();
          this.phraseState = 'idle';
          this.lastPhraseSwitchTime = now;
          this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.config.cyclicTexts.length;
          this.nextPhraseScheduled = false;
        }
        break;
      }
      default:
        break;
    }
  }
}

// Inicialización automática
(function() {
  function initGlitchEffect() {
    console.log('Iniciando efecto glitch...');
    
    const canvas = document.getElementById('glitch-canvas');
    if (!canvas) {
      console.error('Canvas #glitch-canvas no encontrado');
      return;
    }
    
    // Crear instancia con configuración personalizada para el tema
    const glitchBackground = new LetterGlitchBackground({
      glitchColors: ['#20B19E', '#FAF5EF', '#805ae6'], // Colores del tema
      glitchSpeed: 50,
      centerVignette: false,
      outerVignette: true,
      smooth: true,
      characters: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$&*()-_+=/[]{};:<>.,0123456789',
      displayPhrase: true,
      cyclicTexts: ['d3scuentos', 'nquinzenazo', 'of3rtas', 'no lo quieres, lo necesitas'],
      // tamaño más pequeño adicional
      fontSizeDivisor: 68,
      charWidthFactor: 0.8,
      charHeightFactor: 1.2
    });

    // Hacer disponible globalmente
    window.LetterGlitchBackground = LetterGlitchBackground;
    window.glitchBackground = glitchBackground;
    
    console.log('Efecto glitch inicializado correctamente');
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlitchEffect);
  } else {
    initGlitchEffect();
  }
})();